import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  Divider,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/ru';
import { useUnit } from 'effector-react';
import { manualApi } from '../../api/manual';
import { $tenantDataStartDate } from '../../store/auth';
import { formatPeriodRange } from '../../utils/dates';

dayjs.locale('ru');

const STATUS_META = {
  complete: { label: 'Все передано', color: 'success' },
  partial: { label: 'Частично', color: 'warning' },
  none: { label: 'Нет показаний', color: 'error' },
  no_devices: { label: 'Нет приборов', color: 'default' },
};

const SUMMARY_STATUS_FILTERS = [
  {
    key: 'complete',
    getLabel: (summary) => `Квартиры: ${summary.completed}/${summary.total}`,
    color: 'success',
    getCount: (summary) => summary.completed,
  },
  {
    key: 'partial',
    getLabel: (summary) => `Частично: ${summary.partial}`,
    color: 'warning',
    getCount: (summary) => summary.partial,
  },
  {
    key: 'none',
    getLabel: (summary) => `Нет показаний: ${summary.missing}`,
    color: 'error',
    getCount: (summary) => summary.missing,
  },
  {
    key: 'no_devices',
    getLabel: (summary) => `Без приборов: ${summary.noDevices}`,
    color: 'default',
    getCount: (summary) => summary.noDevices,
    hideWhenZero: true,
  },
];

const SOURCE_OPTIONS = [
  { value: 'ANY', label: 'Все источники' },
  { value: 'TELEGRAM', label: 'Telegram' },
  { value: 'IMPORT', label: 'Файл' },
  { value: 'STAFF_USB', label: 'Сбор сотрудником' },
];

const SOURCE_VALUES = new Set(SOURCE_OPTIONS.map((item) => item.value));
const SORT_OPTIONS = [
  { value: 'corp_asc', label: 'Корпус: по возрастанию' },
  { value: 'corp_desc', label: 'Корпус: по убыванию' },
  { value: 'apartment_asc', label: 'Квартира: по возрастанию' },
];
const SORT_VALUES = new Set(SORT_OPTIONS.map((item) => item.value));

const getPeriodStage = (data) => {
  if (!data?.period_start || !data?.period_end) {
    return { label: 'Период не определён', color: 'default' };
  }
  const now = dayjs();
  if (now.isBefore(dayjs(data.period_start))) {
    return { label: 'Ещё не начался', color: 'default' };
  }
  if (data.reading_window_start && now.isBefore(dayjs(data.reading_window_start))) {
    return { label: 'Ожидание сбора', color: 'default' };
  }
  if (!now.isAfter(dayjs(data.period_end))) {
    return { label: 'Сбор идёт', color: 'primary' };
  }
  return { label: 'Завершён', color: 'default' };
};

export default function ManualSubmissionProgressPanel({ buildingId }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tenantDataStartDate = useUnit($tenantDataStartDate);
  const minDate = tenantDataStartDate ? dayjs(tenantDataStartDate) : undefined;
  const queryMonth = searchParams.get('month');
  const initialMonth = /^\d{4}-\d{2}$/.test(queryMonth || '')
    ? queryMonth
    : dayjs().format('YYYY-MM');
  const querySource = searchParams.get('source')?.toUpperCase();
  const initialSource = SOURCE_VALUES.has(querySource) ? querySource : 'ANY';
  const initialCorp = searchParams.get('corp') || 'ALL';
  const querySort = searchParams.get('sort');
  const initialSort = SORT_VALUES.has(querySort) ? querySort : 'corp_asc';
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [progressData, setProgressData] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [corpFilter, setCorpFilter] = useState(initialCorp);
  const [sortMode, setSortMode] = useState(initialSort);
  const [filters, setFilters] = useState({
    month: initialMonth,
    source: initialSource,
  });
  const [monthValue, setMonthValue] = useState(dayjs(`${initialMonth}-01`));

  const loadProgress = useCallback(async () => {
    if (!buildingId) return;
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (filters.month) {
        params.month = filters.month;
      }
      if (filters.source) {
        params.source = filters.source;
      }
      const data = await manualApi.getSubmissionProgress(String(buildingId), params);
      setProgressData(data);
    } catch (err) {
      setError('Не удалось загрузить прогресс передачи показаний.');
    } finally {
      setLoading(false);
    }
  }, [buildingId, filters.month, filters.source]);

  useEffect(() => {
    loadProgress();
  }, [loadProgress]);

  useEffect(() => {
    setStatusFilter(null);
  }, [filters.month, filters.source, buildingId]);

  useEffect(() => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('month', filters.month);
    nextParams.set('source', filters.source);
    if (corpFilter === 'ALL') nextParams.delete('corp');
    else nextParams.set('corp', corpFilter);
    if (sortMode === 'corp_asc') nextParams.delete('sort');
    else nextParams.set('sort', sortMode);
    if (nextParams.toString() !== searchParams.toString()) {
      setSearchParams(nextParams, { replace: true });
    }
  }, [corpFilter, filters.month, filters.source, searchParams, setSearchParams, sortMode]);

  const summary = useMemo(() => {
    if (!progressData) return null;
    const items = (progressData.items || []).filter((item) => {
      if (corpFilter === 'ALL') return true;
      const itemCorp = item.corp_id || `name:${item.corp_name || ''}`;
      return itemCorp === corpFilter;
    });
    const total = items.filter((item) => item.status !== 'no_devices').length;
    const completed = items.filter((item) => item.status === 'complete').length;
    const devicesTotal = items.reduce((sum, item) => sum + (item.total_devices || 0), 0);
    const devicesSubmitted = items.reduce((sum, item) => sum + (item.submitted_devices || 0), 0);
    const partial = items.filter((item) => item.status === 'partial').length;
    const missing = items.filter((item) => item.status === 'none').length;
    const noDevices = items.filter((item) => item.status === 'no_devices').length;
    return {
      total,
      completed,
      apartmentPercent: total ? Math.round((completed / total) * 100) : 0,
      devicePercent: devicesTotal ? Math.round((devicesSubmitted / devicesTotal) * 100) : 0,
      partial,
      missing,
      noDevices,
      devicesTotal,
      devicesSubmitted,
    };
  }, [corpFilter, progressData]);

  const toggleStatusFilter = (status) => {
    setStatusFilter((current) => (current === status ? null : status));
  };

  const collator = useMemo(
    () => new Intl.Collator('ru-RU', { numeric: true, sensitivity: 'base' }),
    []
  );

  const corpOptions = useMemo(() => {
    const options = new Map();
    (progressData?.items || []).forEach((item) => {
      const value = item.corp_id || `name:${item.corp_name || ''}`;
      if (!options.has(value)) {
        options.set(value, item.corp_name || 'Без корпуса');
      }
    });
    return Array.from(options, ([value, label]) => ({ value, label })).sort((a, b) =>
      collator.compare(a.label, b.label)
    );
  }, [collator, progressData]);

  useEffect(() => {
    if (
      corpFilter !== 'ALL' &&
      progressData &&
      !corpOptions.some((option) => option.value === corpFilter)
    ) {
      setCorpFilter('ALL');
    }
  }, [corpFilter, corpOptions, progressData]);

  const sortedItems = useMemo(() => {
    const items = progressData?.items ? [...progressData.items] : [];
    if (!items.length) return items;
    return items.sort((a, b) => {
      const corpA = a.corp_name || '';
      const corpB = b.corp_name || '';
      const corpCompare = collator.compare(corpA, corpB);
      const aptCompare = collator.compare(a.apartment_number || '', b.apartment_number || '');
      if (sortMode === 'apartment_asc') {
        return (
          aptCompare ||
          corpCompare ||
          collator.compare(a.account_number || '', b.account_number || '')
        );
      }
      if (corpCompare !== 0) return sortMode === 'corp_desc' ? -corpCompare : corpCompare;
      if (aptCompare !== 0) return aptCompare;
      return collator.compare(a.account_number || '', b.account_number || '');
    });
  }, [collator, progressData, sortMode]);

  const visibleItems = useMemo(() => {
    return sortedItems.filter((item) => {
      const itemCorp = item.corp_id || `name:${item.corp_name || ''}`;
      if (corpFilter !== 'ALL' && itemCorp !== corpFilter) return false;
      return !statusFilter || item.status === statusFilter;
    });
  }, [corpFilter, sortedItems, statusFilter]);

  const handleSourceChange = (event) => {
    const { value } = event.target;
    setFilters((prev) => ({ ...prev, source: value }));
  };

  const handleMonthChange = (value) => {
    if (!value || !value.isValid?.()) {
      setMonthValue(null);
      setFilters((prev) => ({ ...prev, month: '' }));
      return;
    }
    const nextMonth = value.startOf('month');
    setMonthValue(nextMonth);
    setFilters((prev) => ({ ...prev, month: nextMonth.format('YYYY-MM') }));
  };

  const periodLabel = formatPeriodRange(
    progressData?.period_start,
    progressData?.period_end,
    { timeZone: progressData?.timezone }
  );
  const readingWindowLabel = formatPeriodRange(
    progressData?.reading_window_start,
    progressData?.period_end,
    { timeZone: progressData?.timezone }
  );
  const periodStage = getPeriodStage(progressData);
  const activeFilterLabel = statusFilter ? STATUS_META[statusFilter]?.label : null;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: { xs: 1.25, md: 1.5 },
        mb: 3,
        borderRadius: 2,
        borderColor: 'rgba(148, 163, 184, 0.28)',
        background: 'rgba(255,255,255,0.82)',
        boxShadow: 'none',
        overflow: 'hidden',
      }}
    >
      <Stack spacing={1.5}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          flexWrap="wrap"
          gap={1.5}
        >
          <Stack spacing={0.75} sx={{ minWidth: 260, flex: '1 1 420px' }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                {progressData?.building_name
                  ? `Дом: ${progressData.building_name}`
                  : 'Прогресс передачи показаний'}
              </Typography>
              {summary ? (
                <Chip
                  size="small"
                  color={summary.devicePercent >= 100 ? 'success' : summary.devicePercent > 0 ? 'warning' : 'default'}
                  label={`Приборы ${summary.devicePercent}%`}
                />
              ) : null}
            </Stack>
            {progressData?.building_address ? (
              <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                {progressData.building_address}
              </Typography>
            ) : null}
            {progressData ? (
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap alignItems="center">
                <Chip size="small" variant="outlined" label={`Расчёт: ${periodLabel}`} />
                <Chip
                  size="small"
                  variant="outlined"
                  label={`Окно показаний: ${readingWindowLabel}`}
                />
                <Chip size="small" color={periodStage.color} label={periodStage.label} />
                <Typography variant="caption" color="text.secondary">
                  {`Опорная дата: ${progressData.report_anchor_day || 25} число`}
                  {` · поиск назад: ${progressData.report_fallback_days || 0} дн.`}
                  {progressData.timezone ? `, ${progressData.timezone}` : ''}
                </Typography>
              </Stack>
            ) : null}
            {summary ? (
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap alignItems="center">
                {SUMMARY_STATUS_FILTERS.map((item) => {
                  const count = item.getCount(summary);
                  if (item.hideWhenZero && !count) return null;
                  const selected = statusFilter === item.key;
                  return (
                    <Chip
                      key={item.key}
                      size="small"
                      clickable
                      color={item.color === 'default' ? 'default' : item.color}
                      variant={selected ? 'filled' : 'outlined'}
                      label={item.getLabel(summary)}
                      onClick={() => toggleStatusFilter(item.key)}
                      sx={{
                        cursor: 'pointer',
                        fontWeight: selected ? 800 : 600,
                      }}
                    />
                  );
                })}
                <Chip
                  size="small"
                  variant="outlined"
                  label={`Квартиры: ${summary.completed}/${summary.total} (${summary.apartmentPercent}%)`}
                />
                <Chip
                  size="small"
                  variant="outlined"
                  label={`Приборы: ${summary.devicesSubmitted}/${summary.devicesTotal}`}
                />
                {statusFilter ? (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`Фильтр: ${activeFilterLabel} · ${visibleItems.length}`}
                    onDelete={() => setStatusFilter(null)}
                  />
                ) : null}
              </Stack>
            ) : null}
          </Stack>
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ru">
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <DatePicker
                views={['year', 'month']}
                openTo="month"
                label="Отчётный месяц"
                value={monthValue}
                minDate={minDate}
                onChange={handleMonthChange}
                slotProps={{
                  textField: {
                    sx: { width: 180 },
                    size: 'small',
                  },
                }}
              />
              <TextField
                select
                size="small"
                label="Источник"
                value={filters.source}
                onChange={handleSourceChange}
                sx={{ width: 150 }}
              >
                {SOURCE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
              {corpOptions.length > 1 ? (
                <TextField
                  select
                  size="small"
                  label="Корпус"
                  value={corpFilter}
                  onChange={(event) => setCorpFilter(event.target.value)}
                  sx={{ width: 170 }}
                >
                  <MenuItem value="ALL">Все корпуса</MenuItem>
                  {corpOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              ) : null}
              {corpOptions.length > 1 ? (
                <TextField
                  select
                  size="small"
                  label="Сортировка"
                  value={sortMode}
                  onChange={(event) => setSortMode(event.target.value)}
                  sx={{ width: 220 }}
                >
                  {SORT_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              ) : null}
              <Button size="small" variant="outlined" onClick={loadProgress} disabled={loading}>
                Обновить
              </Button>
            </Stack>
          </LocalizationProvider>
        </Stack>

        {summary ? (
          <Box sx={{ maxWidth: 520 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="caption" color="text.secondary" sx={{ minWidth: 98 }}>
                Прогресс приборов
              </Typography>
              <Box sx={{ flexGrow: 1 }}>
                <LinearProgress
                  variant="determinate"
                  value={summary.devicePercent}
                  sx={{
                    height: 7,
                    borderRadius: 6,
                    backgroundColor: 'rgba(148, 163, 184, 0.18)',
                  }}
                />
              </Box>
              <Typography variant="caption" sx={{ minWidth: 34, textAlign: 'right', fontWeight: 700 }}>
                {summary.devicePercent}%
              </Typography>
            </Stack>
          </Box>
        ) : null}

        {error ? <Typography color="error">{error}</Typography> : null}

        <Divider sx={{ my: 0.25 }} />

        <TableContainer sx={{ maxHeight: 'calc(100vh - 320px)', minHeight: 320, overflowX: 'auto' }}>
          <Table
            stickyHeader
            size="small"
            sx={{
              minWidth: 860,
              '& .MuiTableCell-root': { py: 0.8, px: 1.25 },
              '& .MuiTableHead-root .MuiTableCell-root': {
                fontSize: 12,
                fontWeight: 800,
                color: '#475569',
                backgroundColor: '#F8FAFC',
                borderBottom: '1px solid rgba(148, 163, 184, 0.28)',
              },
              '& .MuiTableBody-root .MuiTableCell-root': {
                borderBottom: '1px solid rgba(226, 232, 240, 0.9)',
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell>Квартира</TableCell>
                <TableCell>Корпус</TableCell>
                <TableCell>Лицевой счёт</TableCell>
                <TableCell sx={{ minWidth: 220 }}>Прогресс</TableCell>
                <TableCell>Передано</TableCell>
                <TableCell>Статус</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading
                ? Array.from({ length: 6 }).map((_, idx) => (
                    <TableRow key={`skeleton-${idx}`}>
                      <TableCell><Skeleton width={80} /></TableCell>
                      <TableCell><Skeleton width={100} /></TableCell>
                      <TableCell><Skeleton width={120} /></TableCell>
                      <TableCell>
                        <Stack spacing={0.5}>
                          <Skeleton variant="rounded" height={8} />
                          <Skeleton width={120} />
                        </Stack>
                      </TableCell>
                      <TableCell><Skeleton width={80} /></TableCell>
                      <TableCell><Skeleton width={110} /></TableCell>
                    </TableRow>
                  ))
                : visibleItems.map((row) => {
                  const meta = STATUS_META[row.status] || STATUS_META.none;
                  const progressValue = Math.round((row.progress || 0) * 100);
                  const progressColor =
                    row.status === 'complete'
                    ? 'success'
                    : row.status === 'partial'
                    ? 'warning'
                    : row.status === 'none'
                    ? 'error'
                    : 'inherit';
                return (
                  <TableRow
                    key={row.apartment_id}
                    hover
                    onClick={() => row.apartment_id && navigate(`/apartments/${row.apartment_id}`)}
                    sx={{ cursor: row.apartment_id ? 'pointer' : 'default' }}
                  >
                    <TableCell>
                      <Typography variant="body2" sx={{ color: '#2368B5', fontWeight: 800 }}>
                        Кв. {row.apartment_number}
                      </Typography>
                    </TableCell>
                    <TableCell>{row.corp_name || '—'}</TableCell>
                    <TableCell>{row.account_number || '—'}</TableCell>
                    <TableCell>
                      <Stack spacing={0.5}>
                        <LinearProgress
                          variant="determinate"
                          value={progressValue}
                          color={progressColor}
                          sx={{ height: 8, borderRadius: 6 }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {row.submitted_devices}/{row.total_devices} приборов
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      {row.submitted_devices} из {row.total_devices}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        clickable
                        label={meta.label}
                        color={meta.color}
                        variant={
                          statusFilter === row.status || meta.color === 'default'
                            ? statusFilter === row.status
                              ? 'filled'
                              : 'outlined'
                            : statusFilter
                              ? 'outlined'
                              : 'filled'
                        }
                        onClick={(event) => {
                          event.stopPropagation();
                          toggleStatusFilter(row.status);
                        }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
              {!loading && !visibleItems.length ? (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography color="text.secondary">
                      {statusFilter
                        ? `Нет квартир со статусом «${activeFilterLabel}».`
                        : corpFilter !== 'ALL'
                          ? 'В выбранном корпусе нет квартир.'
                        : 'Записей пока нет.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </TableContainer>
      </Stack>
    </Paper>
  );
}
